/* eslint-disable*/
import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, matchPath, useLocation } from 'react-router-dom';
import routes from 'src/config/routes';
import { RequireAuth } from 'src/utils/requireAuth';

import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { IRoute, ISidebar } from 'src/interfaces';

import { SideBar } from './components';
import { DistributionModal } from './components/distribution-modal';
import Chatbot from './modules/chatbot';

/** Set REACT_APP_MAINTENANCE_MODE=true in .env to show the maintenance screen for all users. */
const isMaintenanceMode = false;

const MaintenanceView: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      backgroundColor: '#f3f4f8',
      color: '#222247',
      fontFamily: "'Biotif', sans-serif",
      textAlign: 'center',
    }}
  >
    <h1 style={{ fontSize: '2.4rem', fontWeight: 600, margin: '0 0 1rem', color: '#4948ac' }}>
      System under maintenance
    </h1>
    <p style={{ fontSize: '1.8rem', maxWidth: '520px', margin: 0, lineHeight: 1.5 }}>
      We are currently performing scheduled maintenance. The portal will be available again shortly. Thank you for your
      patience.
    </p>
  </div>
);

// Extend auth data type for local use
type AuthDataWithUser = {
  accessToken: string | null;
  modules: string[];
  user?: {
    id: string;
    // add other user properties here if needed
  };
  userRole?: string;
};

const COOKIEBAR_SCRIPT_ID = '__enzuzo-cookiebar-script';
const COOKIEBAR_SCRIPT_SRC = 'https://app.enzuzo.com/scripts/cookiebar/02e5dbd2-1809-11f1-897f-f394f6e7fda1';
const POLICY_PATHS = ['/privacy-policy', '/cookie-policy', '/terms-of-use'];

const CookieBarScriptManager: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const shouldInjectCookieBar = routes.some(route => {
      if (route.privateRoute || POLICY_PATHS.includes(location.pathname)) {
        return false;
      }

      return !!matchPath({ path: route.path, end: true }, location.pathname);
    });

    const existingScript = document.getElementById(COOKIEBAR_SCRIPT_ID);
    if (shouldInjectCookieBar) {
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = COOKIEBAR_SCRIPT_ID;
        script.src = COOKIEBAR_SCRIPT_SRC;
        document.head.prepend(script);
      }
      return;
    }

    if (existingScript) {
      existingScript.remove();
    }
  }, [location.pathname]);

  return null;
};

const RouteElement: React.FC<{
  route: IRoute;
  sidebarData: (ISidebar | undefined)[];
  props: any;
}> = ({ route, sidebarData, props }) => {
  const { data } = useTypedSelector(state => state.config);
  const dataUserRole = useTypedSelector(state => state.auth.data) as AuthDataWithUser;
  const userRolePermission = dataUserRole.userRole;
  if (route.privateRoute) {
    return (
      <RequireAuth permission={route.permission || ''}>
        {!route.fullScreen ? (
          <SideBar sidebarData={sidebarData} {...props} {...route.props}>
            {data.config?.requireDistributionConsent && userRolePermission !== 'external_manager' && (
              <DistributionModal isOpen={true} />
            )}
            <route.element {...props} />
          </SideBar>
        ) : (
          <>
            {data.config?.requireDistributionConsent && userRolePermission !== 'external_manager' && (
              <DistributionModal isOpen={true} />
            )}
            <route.element {...props} />
          </>
        )}
      </RequireAuth>
    );
  }
  return <route.element {...props} />;
};

const Application: React.FC = props => {
  // Cast data to extended type with optional user
  const data = useTypedSelector(state => state.auth.data) as AuthDataWithUser;

  const [token] = useLocalStorage('accessToken', null);
  const { getConfig } = useActions();

  // const setupAppData = async () => {
  //   getConfig();
  // };

  // React.useEffect(() => {
  //   if (token || data.accessToken) {
  //     setupAppData();
  //   }
  // }, [data.accessToken]);

  const setupAppData = async () => {
    // Use local storage token first, fallback to Redux state token
    const activeToken = token || data.accessToken;
    if (activeToken) {
      getConfig(activeToken);
    }
  };

  React.useEffect(() => {
    if (token || data.accessToken) {
      setupAppData();
    }
  }, [token, data.accessToken]);

  const sidebarData = routes.filter(route => route.includeInSidebar).map(route => route.sidebarData);

  if (isMaintenanceMode) {
    return <MaintenanceView />;
  }

  return (
    <div>
      <BrowserRouter>
        <CookieBarScriptManager />
        <Routes>
          {routes.map(route => (
            <Route
              key={route.name}
              path={route.path}
              element={<RouteElement route={route} sidebarData={sidebarData} props={props} />}
            />
          ))}
        </Routes>
      </BrowserRouter>
      <Chatbot userId={''} chatId={''} />
    </div>
  );
};

export default Application;
